import { useId, useState } from 'react';
import { X } from 'lucide-react';
import './TagPicker.css';

/**
 * Chips with suggestions: pick from the datalist or type anything and press
 * Enter. Built for the allergy field, generic enough for any string list.
 */
export function TagPicker({
  label,
  values,
  onChange,
  suggestions,
  placeholder,
  hint,
}: {
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
  suggestions: readonly string[];
  placeholder?: string;
  hint?: string;
}) {
  const listId = useId();
  const inputId = useId();
  const [draft, setDraft] = useState('');

  const add = (raw: string) => {
    const v = raw.trim();
    if (!v) return;
    if (!values.some((x) => x.toLowerCase() === v.toLowerCase())) onChange([...values, v]);
    setDraft('');
  };

  const open = suggestions.filter((s) => !values.some((v) => v.toLowerCase() === s.toLowerCase()));

  return (
    <div className="field">
      <label className="field__label" htmlFor={inputId}>
        {label}
      </label>
      <div className="tagpicker">
        {values.map((v) => (
          <span key={v} className="tagpicker__tag">
            {v}
            <button
              type="button"
              aria-label={`Scoate ${v}`}
              onClick={() => onChange(values.filter((x) => x !== v))}
            >
              <X size={12} />
            </button>
          </span>
        ))}
        <input
          id={inputId}
          list={listId}
          value={draft}
          placeholder={values.length === 0 ? placeholder : undefined}
          onChange={(e) => {
            // Choosing a datalist option fires change with the full value;
            // adding it immediately spares the extra Enter.
            if (open.includes(e.target.value)) add(e.target.value);
            else setDraft(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && draft.trim()) {
              e.preventDefault();
              add(draft);
            } else if (e.key === 'Backspace' && !draft && values.length) {
              onChange(values.slice(0, -1));
            }
          }}
          onBlur={() => add(draft)}
        />
        <datalist id={listId}>
          {open.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
      </div>
      {hint ? <span className="field__hint">{hint}</span> : null}
    </div>
  );
}
